// src/pages/AdminDashboard.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../styles/AdminDashboard.module.css";
import { format } from "date-fns-jalali";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// وارد کردن کامپوننت‌های نقشه از react-leaflet
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css"; // مطمئن شوید این خط در App.js یا index.js شما هم هست

// تنظیم آیکون مارکر پیش‌فرض Leaflet
// این کد مشابه چیزی است که در Register.js دارید و برای نمایش صحیح مارکر ضروری است.
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const AdminDashboard = ({ isAdminLoggedIn, setIsAdminLoggedIn }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // آدرس API به https://backend.battery100.ir حفظ شده است
      const response = await fetch("https://backend.battery100.ir/api/orders");

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "خطا در واکشی سفارشات.");
      }

      const data = await response.json();
      setOrders(data.data);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
      setError(err.message || "خطا در دریافت اطلاعات سفارشات.");
      toast.error(err.message || "خطا در دریافت اطلاعات سفارشات.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdminLoggedIn) {
      navigate("/admin");
      toast.warn("برای دسترسی به پنل ادمین، ابتدا وارد شوید.");
      return;
    }
    fetchOrders();
  }, [isAdminLoggedIn, navigate, fetchOrders]);

  const handleDeleteOrder = async (orderId) => {
    if (
      !window.confirm(
        `آیا مطمئن هستید که می‌خواهید سفارش ${orderId} را حذف کنید؟`
      )
    ) {
      return;
    }

    try {
      // آدرس API به https://backend.battery100.ir حفظ شده است
      const response = await fetch(
        `https://backend.battery100.ir/api/orders/${orderId}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "خطا در حذف سفارش.");
      }

      const result = await response.json();
      toast.success(result.message);
      fetchOrders(); // به‌روزرسانی لیست سفارشات
    } catch (err) {
      console.error("Failed to delete order:", err);
      toast.error(err.message || "خطا در حذف سفارش.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("isAdminLoggedIn"); // حذف پرچم
    setIsAdminLoggedIn(false); // به‌روزرسانی وضعیت
    navigate("/admin");
    toast.info("از پنل ادمین خارج شدید.");
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <span className={styles.spinner}></span>
        <p>در حال بارگذاری سفارشات...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <h2>خطا</h2>
        <p>{error}</p>
        <button onClick={fetchOrders} className={styles.retryButton}>
          تلاش مجدد
        </button>
      </div>
    );
  }

  return (
    <div className={styles.adminDashboardContainer}>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={true}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className={styles.header}>
        <h1 className={styles.title}>پنل مدیریت سفارشات</h1>
        <button onClick={handleLogout} className={styles.logoutButton}>
          خروج
        </button>
      </div>

      {orders.length === 0 ? (
        <div className={styles.noOrders}>
          <p>هیچ سفارشی برای نمایش وجود ندارد.</p>
        </div>
      ) : (
        <div className={styles.ordersList}>
          {orders.map((order) => (
            <div key={order.orderId} className={styles.orderCard}>
              <div className={styles.orderDetails}>
                <h3>شناسه سفارش: {order.orderId}</h3>
                <p>
                  <strong>مشتری:</strong> {order.customer.fullname}
                </p>
                <p>
                  <strong>شماره تماس:</strong> {order.customer.phone}
                </p>
                <p>
                  <strong>آدرس:</strong> {order.customer.address}
                </p>
                {order.customer.postalCode && (
                  <p>
                    <strong>کد پستی:</strong> {order.customer.postalCode}
                  </p>
                )}
                {order.customer.additionalInfo && (
                  <p>
                    <strong>توضیحات اضافی:</strong>{" "}
                    {order.customer.additionalInfo}
                  </p>
                )}
                <p>
                  <strong>تاریخ سفارش:</strong>{" "}
                  {format(new Date(order.orderDate), "yyyy/MM/dd HH:mm")}
                </p>
                <p>
                  <strong>مجموع مبلغ:</strong>{" "}
                  {order.totalAmount.toLocaleString()} تومان
                </p>
                <p>
                  <strong>وضعیت:</strong>{" "}
                  <span
                    className={`${styles.statusBadge} ${styles[order.status]}`}
                  >
                    {order.status}
                  </span>
                </p>
              </div>
              <div className={styles.orderItems}>
                <h4>آیتم‌های سفارش:</h4>
                <ul>
                  {order.items.map((item, index) => (
                    <li key={index}>
                      <img
                        src={item.image}
                        alt={item.name}
                        className={styles.itemImageSmall}
                      />
                      <span>
                        {item.name} ({item.quantity} عدد)
                      </span>
                      <span>{item.price.toLocaleString()} تومان</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* بخش نقشه برای هر سفارش */}
              {order.customer.location &&
              order.customer.location.lat &&
              order.customer.location.lng ? (
                <div className={styles.orderMapContainer}>
                  <h4>موقعیت تحویل:</h4>
                  <MapContainer
                    center={[
                      order.customer.location.lat,
                      order.customer.location.lng,
                    ]}
                    zoom={15} // زوم مناسب برای نمایش یک آدرس خاص
                    scrollWheelZoom={false} // غیرفعال کردن زوم با اسکرول موس
                    dragging={false} // غیرفعال کردن درگ نقشه
                    zoomControl={false} // غیرفعال کردن کنترل‌های زوم
                    doubleClickZoom={false} // غیرفعال کردن زوم با دابل کلیک
                    attributionControl={false} // غیرفعال کردن نمایش "OpenStreetMap contributors"
                    className={styles.miniMap}
                    key={order.orderId} // کلید منحصر به فرد برای هر نقشه
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <Marker
                      position={[
                        order.customer.location.lat,
                        order.customer.location.lng,
                      ]}
                    >
                      <Popup>
                        موقعیت تحویل سفارش <br /> {order.customer.address}
                      </Popup>
                    </Marker>
                  </MapContainer>
                </div>
              ) : (
                <p className={styles.noLocation}>
                  موقعیت مکانی برای این سفارش ثبت نشده است.
                </p>
              )}

              <button
                onClick={() => handleDeleteOrder(order.orderId)}
                className={styles.deleteButton}
              >
                حذف سفارش
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
